import React, {useCallback, useEffect, useState} from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import LabelIcon from '@mui/icons-material/Label';
import * as Yup from 'yup';
import {AcquisitionTypeDTO, AttributeDTO, CommentDTO, FileMetadataDTO} from '../../api/generated/requests/types.gen';
import {useContactById, useContactMutations} from '../../hooks/useContactData';
import {useFileMutations, useRelatedFiles} from '../../hooks/useFileData';
import FileList from '../Common/FileList';
import {MentionInput} from '../Common/MentionInput';
import {MentionText} from '../Common/MentionText';
import {useQueryClient} from '@tanstack/react-query';
import {CommentsService} from '../../api/generated/requests/services.gen';
import {format} from 'date-fns';
import {pl} from 'date-fns/locale';
import {useSnackbar} from 'notistack';
import {
  useAcquisitionTypesServiceGetAcquisitionTypes,
  useAttributesServiceDeleteApiAttributesById,
  useAttributesServiceGetApiAttributesObjectByObjectTypeByObjectId,
  useAttributesServicePostApiAttributesBulk,
  useAttributesServicePutApiAttributesBulk,
  useLeadAttributesServiceGetApiPredefinedLeadAttributes,
  useOffersServiceGetApiOffers,
  useTaskRetrievalServiceGetApiLeadTasksLeadByLeadIdTasks,
  useUserServiceGetApiUserMe
} from '../../api/generated/queries/queries';
import { useTaskRetrievalServiceGetApiLeadTasksLeadByLeadIdTasksKey } from '../../api/generated/queries/common';
import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  AttachFile as AttachFileIcon,
  Cancel as CancelIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Comment as CommentIcon,
  Create as CreateIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  Task as TaskIcon
} from '@mui/icons-material';
import TaskDetailDialog from '../Tasks/TaskDetailDialog';
import NewTaskButton from '../Tasks/NewTaskButton';
import {getStatusColor, getStatusLabel} from '../../models/TaskDTO';
import {useUserData} from '../../hooks/useUserData';

interface LeadDetailDialogProps {
  open: boolean;
  leadId: number | null;
  onClose: () => void;
  onLeadUpdated?: () => void;
  initialTab?: number;
  highlightCommentId?: number | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`lead-detail-tabpanel-${index}`}
      aria-labelledby={`lead-detail-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 0.5, minHeight: '450px' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `lead-detail-tab-${index}`,
    'aria-controls': `lead-detail-tabpanel-${index}`,
  };
}
Yup.object({
  firstName: Yup.string().required('Imię jest wymagane'),
  lastName: Yup.string().required('Nazwisko jest wymagane'),
  email: Yup.string().email('Niepoprawny format email').required('Email jest wymagany'),
  phone: Yup.string().matches(/^[0-9\s+-]{9,15}$/, 'Niepoprawny format numeru telefonu').required('Telefon jest wymagany'),
});
const LeadDetailDialog: React.FC<LeadDetailDialogProps> = ({ open, leadId, onClose, onLeadUpdated = () => {}, initialTab = 0, highlightCommentId = null }) => {
  // Use optimized hook for fetching contact data
  const { data: lead, isLoading: loading, refetch: refetchLead} = useContactById(leadId as number);
  const { updateContact } = useContactMutations();
  const { files, isLoading: loadingFiles, refetch: refetchFiles } = useRelatedFiles(leadId as number, 'LEAD');
  const { uploadFile, deleteFile, restoreFile, downloadFile, isUploading, isDeleting } = useFileMutations();
  const queryClient = useQueryClient();
  const [tabValue, setTabValue] = useState(initialTab);
  const [comments, setComments] = useState<CommentDTO[]>([]);
  const [newComment, setNewComment] = useState('');
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);

  // Task-related state and data fetching
  const { data: leadTasks, isLoading: loadingTasks, refetch: refetchTasks } = useTaskRetrievalServiceGetApiLeadTasksLeadByLeadIdTasks(
    { leadId: leadId || 0 }, 
    undefined, 
    { enabled: !!leadId && leadId !== null && leadId !== undefined && leadId > 0 }
  );
  const [openTaskDetailDialog, setOpenTaskDetailDialog] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);

  // Update tabValue when initialTab changes
  useEffect(() => {
    setTabValue(initialTab);
  }, [initialTab]);

  // Force fresh data fetch when dialog opens - this handles consecutive same contact opens
  useEffect(() => {
    if (open && leadId && leadId > 0 && refetchTasks) {
      // Only manually refetch tasks since useContactById and useRelatedFiles 
      // already have refetchOnMount: true which automatically refetches when dialog opens
      // This prevents duplicate API calls for lead-contacts and by-related-object endpoints
      refetchTasks();
    }
  }, [open, leadId, refetchTasks]);

  // Reset component state when leadId changes to prevent showing stale data
  useEffect(() => {
    if (leadId) {
      // Clear previous state when a new contact is selected
      setComments([]);
      setNewComment('');
      setMentionedUserIds([]);
      setExpandedComments({});
      setShowAllComments(false);
      setEditingCommentId(null);
      setEditedCommentText('');
      
      // Don't reset attributes state here to prevent race condition
      // Attributes will be properly updated when existingAttributes data arrives
      // setAttributes({});
      // setAttributeIds({});
      // setAttributeEditModes({});
      // setEditedAttributes({});

      // Reset all edit modes
      setFirstNameEditMode(false);
      setLastNameEditMode(false);
      setNameEditMode(false);
      setEmailEditMode(false);
      setPhoneEditMode(false);
      setAlternatePhoneEditMode(false);
      setCityEditMode(false);
      setPostalCodeEditMode(false);
      setStreetEditMode(false);
      setHouseNumberEditMode(false);
      setApartmentNumberEditMode(false);
      setNipEditMode(false);

      // Close any open menus
      setAcquisitionTypeMenuAnchor(null);
      setOfferMenuAnchor(null);
    }
  }, [leadId]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [expandedComments, setExpandedComments] = useState<Record<number, boolean>>({});
  const [showAllComments, setShowAllComments] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editedCommentText, setEditedCommentText] = useState('');
  const { data: acquisitionTypes, isLoading: isLoadingAcquisitionTypes } = useAcquisitionTypesServiceGetAcquisitionTypes();
  const { data: offers, isLoading: isLoadingOffers } = useOffersServiceGetApiOffers();
  const { data: currentUser } = useUserServiceGetApiUserMe();
  
  // Attributes state and API calls
  const [attributes, setAttributes] = useState<Record<string, string>>({});
  const [attributeIds, setAttributeIds] = useState<Record<string, number>>({});
  const [attributeEditModes, setAttributeEditModes] = useState<Record<string, boolean>>({});
  const [editedAttributes, setEditedAttributes] = useState<Record<string, string>>({});
  const [selectedAttributeKeys, setSelectedAttributeKeys] = useState<Set<string>>(new Set());
  const [attributeMenuAnchor, setAttributeMenuAnchor] = useState<null | HTMLElement>(null);
  
  const { data: predefinedAttributes, isLoading: isLoadingPredefinedAttributes } = 
    useLeadAttributesServiceGetApiPredefinedLeadAttributes();
  const { data: existingAttributes, isLoading: isLoadingExistingAttributes, refetch: refetchExistingAttributes } = 
    useAttributesServiceGetApiAttributesObjectByObjectTypeByObjectId(
      { objectType: 'LEAD', objectId: leadId || 0 },
      [],
      { 
        enabled: !!leadId,
        staleTime: 0, // Always consider data stale to ensure fresh fetch
        gcTime: 0, // Don't cache data
        refetchOnWindowFocus: false, // Don't refetch when window gains focus
        refetchOnMount: true, // Always refetch when component mounts
        refetchOnReconnect: true, // Refetch when reconnecting
        retry: false // Don't retry failed requests
      }
    );

  // User data for displaying colored avatars
  const { domainUsers } = useUserData();

  // Helper function to get user initials
  const getUserInitials = (userId?: string) => {
    if (!userId) return '';

    const user = domainUsers.find(u => u.id === userId);
    if (!user) return '';

    const firstName = user.firstName || '';
    const lastName = user.lastName || '';

    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Helper function to get user color
  const getUserColor = (userId?: string) => {
    if (!userId) return 'grey.300';

    const user = domainUsers.find(u => u.id === userId);
    if (!user) return 'grey.300';

    return user.color || 'primary.main';
  };

  // Helper function to get user full name
  const getUserFullName = (userId?: string) => {
    if (!userId) return 'Nieprzypisane';

    const user = domainUsers.find(u => u.id === userId);
    if (!user) return 'Nieznany użytkownik';

    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.login || 'Nieznany użytkownik';
  };


  
  // Mutation hooks for attribute operations
  const postAttributesBulk = useAttributesServicePostApiAttributesBulk();
  const putAttributesBulk = useAttributesServicePutApiAttributesBulk();
  const deleteAttribute = useAttributesServiceDeleteApiAttributesById();

  // Helper functions for attribute management
  const handleAddAttribute = (attributeKey: string) => {
    setSelectedAttributeKeys(prev => {
      const newSet = new Set(prev);
      newSet.add(attributeKey);
      return newSet;
    });
    setAttributes(prev => ({ ...prev, [attributeKey]: '' }));
    setEditedAttributes(prev => ({ ...prev, [attributeKey]: '' }));
    setAttributeEditModes(prev => ({ ...prev, [attributeKey]: true }));
    setAttributeMenuAnchor(null);
  };

  const handleRemoveAttribute = async (attributeKey: string) => {
    const attributeId = attributeIds[attributeKey];
    
    // If attribute has an ID, delete it from backend first
    if (attributeId) {
      try {
        await deleteAttribute.mutateAsync({ id: attributeId });
        enqueueSnackbar('Atrybut został usunięty', { variant: 'success' });
      } catch (error) {
        console.error('Error deleting attribute:', error);
        enqueueSnackbar('Błąd podczas usuwania atrybutu', { variant: 'error' });
        return; // Don't update local state if backend deletion failed
      }
    }
    
    // Update local state only after successful backend deletion (or if no ID exists)
    setSelectedAttributeKeys(prev => {
      const newSet = new Set(prev);
      newSet.delete(attributeKey);
      return newSet;
    });
    setAttributes(prev => {
      const newAttributes = { ...prev };
      delete newAttributes[attributeKey];
      return newAttributes;
    });
    setEditedAttributes(prev => {
      const newEditedAttributes = { ...prev };
      delete newEditedAttributes[attributeKey];
      return newEditedAttributes;
    });
    setAttributeEditModes(prev => {
      const newEditModes = { ...prev };
      delete newEditModes[attributeKey];
      return newEditModes;
    });
    setAttributeIds(prev => {
      const newAttributeIds = { ...prev };
      delete newAttributeIds[attributeKey];
      return newAttributeIds;
    });
  };

  const getAvailableAttributes = () => {
    return predefinedAttributes?.filter(attr => 
      attr.attributeKey && !selectedAttributeKeys.has(attr.attributeKey)
    ) || [];
  };

  // Populate attributes state when existing attributes are loaded
  useEffect(() => {
    if (existingAttributes) {
      const attributesMap: Record<string, string> = {};
      const attributeIdsMap: Record<string, number> = {};
      const editedAttributesMap: Record<string, string> = {};
      const selectedKeys = new Set<string>();
      
      existingAttributes.forEach(attr => {
        if (attr.attributeKey && attr.attributeValue && attr.id) {
          attributesMap[attr.attributeKey] = attr.attributeValue;
          attributeIdsMap[attr.attributeKey] = attr.id;
          editedAttributesMap[attr.attributeKey] = attr.attributeValue;
          selectedKeys.add(attr.attributeKey);
        }
      });
      
      setAttributes(attributesMap);
      setAttributeIds(attributeIdsMap);
      setEditedAttributes(editedAttributesMap);
      setSelectedAttributeKeys(selectedKeys);
    }
  }, [existingAttributes]);

  // Initialize edit states for predefined attributes
  useEffect(() => {
    if (predefinedAttributes) {
      const editedAttrsMap: Record<string, string> = {};
      predefinedAttributes.forEach(attr => {
        if (attr.attributeKey) {
          // Use current attributes value if available, otherwise empty string
          editedAttrsMap[attr.attributeKey] = attributes[attr.attributeKey] || '';
        }
      });
      
      // Only initialize edit modes for attributes that don't already have edit modes set
      // This preserves edit modes that were set by handleAddAttribute
      setAttributeEditModes(prev => {
        const newEditModes = { ...prev };
        predefinedAttributes.forEach(attr => {
          if (attr.attributeKey && !(attr.attributeKey in prev)) {
            // Only set to false if the attribute doesn't already have an edit mode
            newEditModes[attr.attributeKey] = false;
          }
        });
        return newEditModes;
      });
      
      // Only update editedAttributes for keys that don't already have values
      // This prevents overwriting populated values with empty ones
      setEditedAttributes(prev => {
        const newEditedAttrs = { ...prev };
        Object.keys(editedAttrsMap).forEach(key => {
          // Only update if the key doesn't exist or if we have a non-empty value
          if (!prev[key] || editedAttrsMap[key]) {
            newEditedAttrs[key] = editedAttrsMap[key];
          }
        });
        return newEditedAttrs;
      });
    }
  }, [predefinedAttributes, attributes]);

  
  // Inline editing states
  const [firstNameEditMode, setFirstNameEditMode] = useState(false);
  const [lastNameEditMode, setLastNameEditMode] = useState(false);
  const [nameEditMode, setNameEditMode] = useState(false);
  const [emailEditMode, setEmailEditMode] = useState(false);
  const [phoneEditMode, setPhoneEditMode] = useState(false);
  const [alternatePhoneEditMode, setAlternatePhoneEditMode] = useState(false);
  const [cityEditMode, setCityEditMode] = useState(false);
  const [postalCodeEditMode, setPostalCodeEditMode] = useState(false);
  const [streetEditMode, setStreetEditMode] = useState(false);
  const [houseNumberEditMode, setHouseNumberEditMode] = useState(false);
  const [apartmentNumberEditMode, setApartmentNumberEditMode] = useState(false);
  const [nipEditMode, setNipEditMode] = useState(false);
  const [acquisitionTypeMenuAnchor, setAcquisitionTypeMenuAnchor] = useState<HTMLElement | null>(null);
  const [offerMenuAnchor, setOfferMenuAnchor] = useState<HTMLElement | null>(null);
  const [editedFirstName, setEditedFirstName] = useState('');
  const [editedLastName, setEditedLastName] = useState('');
  const [editedName, setEditedName] = useState('');
  const [editedEmail, setEditedEmail] = useState('');
  const [editedPhone, setEditedPhone] = useState('');
  const [editedAlternatePhone, setEditedAlternatePhone] = useState('');
  const [editedCity, setEditedCity] = useState('');
  const [editedPostalCode, setEditedPostalCode] = useState('');
  const [editedStreet, setEditedStreet] = useState('');
  const [editedHouseNumber, setEditedHouseNumber] = useState('');
  const [editedApartmentNumber, setEditedApartmentNumber] = useState('');
  const [editedNip, setEditedNip] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const dialogContentRef = React.useRef<HTMLDivElement>(null);

  const fetchComments = useCallback(async () => {
    if (!leadId) return;

    setLoadingComments(true);
    try {
      const commentsData = await CommentsService.getApiComments({
        objectId: leadId,
        objectType: 'LEAD'
      });
      // Sort comments by date in descending order (newest first)
      const sortedComments = [...commentsData].sort((a, b) => {
        const dateA = new Date(a.createdAt || '');
        const dateB = new Date(b.createdAt || '');
        return dateB.getTime() - dateA.getTime();
      });

      setComments(sortedComments);

      // Reset expanded comments state
      const initialExpandedState: Record<number, boolean> = {};
      sortedComments.forEach(comment => {
        if (comment.id) {
          initialExpandedState[comment.id] = true; // Set to true to make comments expanded by default
        }
      });
      setExpandedComments(initialExpandedState);

      // Reset show all comments state
      setShowAllComments(false);
    } catch (error) {
      console.error('Error fetching comments:', error);
      enqueueSnackbar('Nie udało się pobrać komentarzy', { variant: 'error' });
    } finally {
      setLoadingComments(false);
    }
  }, [leadId, enqueueSnackbar]);


  // Update edited state variables when lead data changes
  // Only update fields that are NOT currently in edit mode
  useEffect(() => {
    if (lead) {
      // Only update field values if they are not currently being edited
      if (!firstNameEditMode) {
        setEditedFirstName(lead.firstName || '');
      }
      if (!lastNameEditMode) {
        setEditedLastName(lead.lastName || '');
      }
      if (!nameEditMode) {
        setEditedName(lead.name || '');
      }
      if (!emailEditMode) {
        setEditedEmail(lead.email || '');
      }
      if (!phoneEditMode) {
        setEditedPhone(lead.phone || '');
      }
      if (!alternatePhoneEditMode) {
        setEditedAlternatePhone(lead.alternatePhone || '');
      }
      if (!cityEditMode) {
        setEditedCity(lead.city || '');
      }
      if (!postalCodeEditMode) {
        setEditedPostalCode(lead.postalCode || '');
      }
      if (!streetEditMode) {
        setEditedStreet(lead.street || '');
      }
      if (!houseNumberEditMode) {
        setEditedHouseNumber(lead.houseNumber || '');
      }
      if (!apartmentNumberEditMode) {
        setEditedApartmentNumber(lead.apartmentNumber || '');
      }
      if (!nipEditMode) {
        setEditedNip(lead.nip || '');
      }
    }
  }, [lead, firstNameEditMode, lastNameEditMode, nameEditMode, emailEditMode, phoneEditMode, alternatePhoneEditMode, cityEditMode, postalCodeEditMode, streetEditMode, houseNumberEditMode, apartmentNumberEditMode, nipEditMode]);

  // Fetch comments when dialog opens
  useEffect(() => {
    if (open && leadId) {
      fetchComments();
    }
  }, [open, leadId, fetchComments]);

  // Comment highlighting effect when navigating from search
  useEffect(() => {
    if (highlightCommentId && comments.length > 0 && tabValue === 1) {
      const expandCommentsUntilVisible = async () => {
        let attempts = 0;
        const maxAttempts = 10; // Zabezpieczenie przed nieskończoną pętlą
        
        while (attempts < maxAttempts) {
          // Sprawdź czy komentarz jest już widoczny w DOM
          const commentElement = document.querySelector(`[data-comment-id="${highlightCommentId}"]`);
          
          if (commentElement) {
            // Komentarz znaleziony - podświetl go
            setTimeout(() => {
              commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              commentElement.classList.add('highlighted-comment');
              setTimeout(() => {
                commentElement.classList.remove('highlighted-comment');
              }, 2500);
            }, 100);
            break;
          }
          
          // Jeśli komentarz nie jest widoczny, sprawdź czy jest przycisk "Zobacz więcej"
          if (!showAllComments) {
            setShowAllComments(true);
            // Poczekaj na rerenderowanie po ustawieniu showAllComments
            await new Promise(resolve => setTimeout(resolve, 300));
            attempts++;
          } else {
            // showAllComments jest już true, ale komentarz nie jest widoczny
            // Prawdopodobnie komentarz nie istnieje
            console.warn(`Komentarz o ID ${highlightCommentId} nie został znaleziony`);
            break;
          }
        }
        
        if (attempts >= maxAttempts) {
          console.warn(`Przekroczono maksymalną liczbę prób (${maxAttempts}) wyszukiwania komentarza o ID ${highlightCommentId}`);
        }
      };
      
      // Dodaj małe opóźnienie na wyrenderowanie komponentu
      const timeoutId = setTimeout(() => {
        expandCommentsUntilVisible();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [highlightCommentId, comments, tabValue, showAllComments]);

  // Add CSS styles for comment highlighting
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .highlighted-comment {
        background-color: #fff3cd !important;
        border: 2px solid #ffc107 !important;
        animation: commentHighlightPulse 2.5s ease-in-out;
      }
      
      @keyframes commentHighlightPulse {
        0% { background-color: #fff3cd; }
        50% { background-color: #ffeaa7; }
        100% { background-color: #fff3cd; }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Listen for global refresh-tasks event to update task list when new task is created
  useEffect(() => {
    const handleRefreshTasks = () => {
      if (refetchTasks && leadId && leadId > 0) {
        refetchTasks();
      }
    };

    window.addEventListener('refresh-tasks', handleRefreshTasks);

    return () => {
      window.removeEventListener('refresh-tasks', handleRefreshTasks);
    };
  }, [refetchTasks, leadId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Inline editing handlers
  const handleFirstNameClick = () => {
      setFirstNameEditMode(true);
      setEditedFirstName(lead?.firstName || '');
  };

  const handleLastNameClick = () => {
      setLastNameEditMode(true);
      setEditedLastName(lead?.lastName || '');
  };

  const handleEmailClick = () => {
      setEmailEditMode(true);
      setEditedEmail(lead?.email || '');
  };

  const handlePhoneClick = () => {
      setPhoneEditMode(true);
      setEditedPhone(lead?.phone || '');
  };

  const handleAlternatePhoneClick = () => {
      setAlternatePhoneEditMode(true);
      setEditedAlternatePhone(lead?.alternatePhone || '');
  };

  const handleNameClick = () => {
      setNameEditMode(true);
      setEditedName(lead?.name || '');
  };

  const handleNipClick = () => {
      setNipEditMode(true);
      setEditedNip(lead?.nip || '');
  };

  const handleCityClick = () => {
      setCityEditMode(true);
      setEditedCity(lead?.city || '');
  };

  const handlePostalCodeClick = () => {
      setPostalCodeEditMode(true);
      setEditedPostalCode(lead?.postalCode || '');
  };

  const handleStreetClick = () => {
      setStreetEditMode(true);
      setEditedStreet(lead?.street || '');
  };

  const handleHouseNumberClick = () => {
      setHouseNumberEditMode(true);
      setEditedHouseNumber(lead?.houseNumber || '');
  };

  const handleApartmentNumberClick = () => {
      setApartmentNumberEditMode(true);
      setEditedApartmentNumber(lead?.apartmentNumber || '');
  };

  const handleSaveFirstName = () => {
    if (!leadId || !lead) return;

    const updatedLead = { ...lead, firstName: editedFirstName };
    updateContact({
      id: leadId,
      requestBody: updatedLead
    });

    setFirstNameEditMode(false);
    // Update the lead object immediately to show the correct value
    Object.assign(lead, { firstName: editedFirstName });
  };

  const handleSaveLastName = () => {
    if (!leadId || !lead) return;

    const updatedLead = { ...lead, lastName: editedLastName };
    updateContact({
      id: leadId,
      requestBody: updatedLead
    });

    setLastNameEditMode(false);
    // Update the lead object immediately to show the correct value
    Object.assign(lead, { lastName: editedLastName });
  };

  const handleSaveEmail = () => {
    if (!leadId || !lead) return;

    const updatedLead = { ...lead, email: editedEmail };
    updateContact({
      id: leadId,
      requestBody: updatedLead
    });

    setEmailEditMode(false);
    // Update the lead object immediately to show the correct value
    Object.assign(lead, { email: editedEmail });
  };

  const handleSavePhone = () => {
    if (!leadId || !lead) return;

    const updatedLead = { ...lead, phone: editedPhone };
    updateContact({
      id: leadId,
      requestBody: updatedLead
    });

    setPhoneEditMode(false);
    // Update the lead object immediately to show the correct value
    Object.assign(lead, { phone: editedPhone });
  };

  const handleSaveAlternatePhone = () => {
    if (!leadId || !lead) return;

    const updatedLead = { ...lead, alternatePhone: editedAlternatePhone };
    updateContact({
      id: leadId,
      requestBody: updatedLead
    });

    setAlternatePhoneEditMode(false);
    // Update the lead object immediately to show the correct value
    Object.assign(lead, { alternatePhone: editedAlternatePhone });
  };

  const handleSaveName = () => {
    if (!leadId || !lead) return;

    const updatedLead = { ...lead, name: editedName };
    updateContact({
      id: leadId,
      requestBody: updatedLead
    });

    setNameEditMode(false);
    // Update the lead object immediately to show the correct value
    Object.assign(lead, { name: editedName });
  };

  const handleSaveNip = () => {
    if (!leadId || !lead) return;

    const updatedLead = { ...lead, nip: editedNip };
    updateContact({
      id: leadId,
      requestBody: updatedLead
    });

    setNipEditMode(false);
    // Update the lead object immediately to show the correct value
    Object.assign(lead, { nip: editedNip });
  };

  const handleSaveCity = () => {
    if (!leadId || !lead) return;

    const updatedLead = { ...lead, city: editedCity };
    updateContact({
      id: leadId,
      requestBody: updatedLead
    });

    setCityEditMode(false);
    // Update the lead object immediately to show the correct value
    Object.assign(lead, { city: editedCity });
  };

  const handleSavePostalCode = () => {
    if (!leadId || !lead) return;

    const updatedLead = { ...lead, postalCode: editedPostalCode };
    updateContact({
      id: leadId,
      requestBody: updatedLead
    });

    setPostalCodeEditMode(false);
    // Update the lead object immediately to show the correct value
    Object.assign(lead, { postalCode: editedPostalCode });
  };

  const handleSaveStreet = () => {
    if (!leadId || !lead) return;

    const updatedLead = { ...lead, street: editedStreet };
    updateContact({
      id: leadId,
      requestBody: updatedLead
    });

    setStreetEditMode(false);
    // Update the lead object immediately to show the correct value
    Object.assign(lead, { street: editedStreet });
  };

  const handleSaveHouseNumber = () => {
    if (!leadId || !lead) return;

    const updatedLead = { ...lead, houseNumber: editedHouseNumber };
    updateContact({
      id: leadId,
      requestBody: updatedLead
    });

    setHouseNumberEditMode(false);
    // Update the lead object immediately to show the correct value
    Object.assign(lead, { houseNumber: editedHouseNumber });
  };

  const handleSaveApartmentNumber = () => {
    if (!leadId || !lead) return;

    const updatedLead = { ...lead, apartmentNumber: editedApartmentNumber };
    updateContact({
      id: leadId,
      requestBody: updatedLead
    });

    setApartmentNumberEditMode(false);
    // Update the lead object immediately to show the correct value
    Object.assign(lead, { apartmentNumber: editedApartmentNumber });
  };

  const handleCancelFirstNameEdit = () => {
    setFirstNameEditMode(false);
    setEditedFirstName(lead?.firstName || '');
  };

  const handleCancelLastNameEdit = () => {
    setLastNameEditMode(false);
    setEditedLastName(lead?.lastName || '');
  };

  const handleCancelEmailEdit = () => {
    setEmailEditMode(false);
    setEditedEmail(lead?.email || '');
  };

  const handleCancelPhoneEdit = () => {
    setPhoneEditMode(false);
    setEditedPhone(lead?.phone || '');
  };

  const handleCancelAlternatePhoneEdit = () => {
    setAlternatePhoneEditMode(false);
    setEditedAlternatePhone(lead?.alternatePhone || '');
  };

  const handleCancelNameEdit = () => {
    setNameEditMode(false);
    setEditedName(lead?.name || '');
  };

  const handleCancelNipEdit = () => {
    setNipEditMode(false);
    setEditedNip(lead?.nip || '');
  };

  const handleCancelCityEdit = () => {
    setCityEditMode(false);
    setEditedCity(lead?.city || '');
  };

  const handleCancelPostalCodeEdit = () => {
    setPostalCodeEditMode(false);
    setEditedPostalCode(lead?.postalCode || '');
  };

  const handleCancelStreetEdit = () => {
    setStreetEditMode(false);
    setEditedStreet(lead?.street || '');
  };

  const handleCancelHouseNumberEdit = () => {
    setHouseNumberEditMode(false);
    setEditedHouseNumber(lead?.houseNumber || '');
  };

  const handleCancelApartmentNumberEdit = () => {
    setApartmentNumberEditMode(false);
    setEditedApartmentNumber(lead?.apartmentNumber || '');
  };

  // Attribute inline editing handlers
  const handleAttributeClick = (attributeKey: string) => {
    setAttributeEditModes(prev => ({ ...prev, [attributeKey]: true }));
    setEditedAttributes(prev => ({ ...prev, [attributeKey]: attributes[attributeKey] || '' }));
  };

  const handleSaveAttribute = async (attributeKey: string) => {
    if (!leadId) return;

    // Check if attribute already exists (declare outside try block for catch access)
    const existingId = attributeIds[attributeKey];

    try {
      // Find the attributeType from predefined attributes
      const predefinedAttr = predefinedAttributes?.find(attr => attr.attributeKey === attributeKey);
      const attributeType = predefinedAttr?.attributeType;
      
      const attributeData: AttributeDTO = {
        ...(existingId && { id: existingId }),
        attributeKey: attributeKey,
        attributeValue: editedAttributes[attributeKey],
        attributeType: attributeType,
        relatedObjectId: leadId,
        relatedObjectType: 'LEAD' as const
      };

      let result: AttributeDTO[];
      if (existingId) {
        // Update existing attribute using PUT
        result = await putAttributesBulk.mutateAsync({
          requestBody: [attributeData]
        });
        enqueueSnackbar('Atrybut został zaktualizowany', { variant: 'success' });
      } else {
        // Create new attribute using POST
        result = await postAttributesBulk.mutateAsync({
          requestBody: [attributeData]
        });
        
        // Store the new attribute ID for future updates
        if (result && result.length > 0 && result[0].id) {
          setAttributeIds(prev => ({ ...prev, [attributeKey]: result[0].id! }));
        }
        
        enqueueSnackbar('Atrybut został utworzony', { variant: 'success' });
      }

      // Update local state
      setAttributes(prev => ({ ...prev, [attributeKey]: editedAttributes[attributeKey] }));
      setAttributeEditModes(prev => ({ ...prev, [attributeKey]: false }));
      
    } catch (error) {
      console.error('Error saving attribute:', error);
      const action = existingId ? 'zaktualizować' : 'utworzyć';
      enqueueSnackbar(`Nie udało się ${action} atrybutu`, { variant: 'error' });
    }
  };

  const handleCancelAttributeEdit = (attributeKey: string) => {
    setAttributeEditModes(prev => ({ ...prev, [attributeKey]: false }));
    setEditedAttributes(prev => ({ ...prev, [attributeKey]: attributes[attributeKey] || '' }));
  };

  const handleAcquisitionTypeChange = (acquisitionType: AcquisitionTypeDTO) => {
    if (!leadId || !lead) return;

    const updatedLead = { ...lead, acquisitionType };
    updateContact({
      id: leadId,
      requestBody: updatedLead
    });

    setAcquisitionTypeMenuAnchor(null);
  };

  const handleOfferChange = (offerId: number | null) => {
    if (!leadId || !lead) return;

    const updatedLead = { ...lead, offer: offerId || undefined };
    updateContact({
      id: leadId,
      requestBody: updatedLead
    });

    setOfferMenuAnchor(null);
  };

  const handleAddComment = async () => {
    if (!leadId || !newComment.trim()) return;

    try {
      const commentData: CommentDTO = {
        description: newComment,
        objectId: leadId,
        objectType: 'LEAD',
        mentionedUserIds: mentionedUserIds.length > 0 ? mentionedUserIds : undefined
      };

      const response = await CommentsService.postApiComments({
        requestBody: commentData
      });

      setComments([response, ...comments]);

      // Set the new comment to be expanded by default
      if (response.id) {
        setExpandedComments(prev => ({
          ...prev,
          [response.id as number]: true
        }));
      }

      setNewComment('');
      setMentionedUserIds([]);
      enqueueSnackbar('Komentarz został dodany', { variant: 'success' });
    } catch (error) {
      console.error('Error adding comment:', error);
      enqueueSnackbar('Nie udało się dodać komentarza', { variant: 'error' });
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      await CommentsService.deleteApiCommentsById({ id: commentId });
      setComments(comments.filter(comment => comment.id !== commentId));
      enqueueSnackbar('Komentarz został usunięty', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting comment:', error);
      enqueueSnackbar('Nie udało się usunąć komentarza', { variant: 'error' });
    }
  };

  const toggleCommentExpand = (commentId: number) => {
    setExpandedComments(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  };

  const handleEditComment = (comment: CommentDTO) => {
    if (comment.id) {
      setEditingCommentId(comment.id);
      setEditedCommentText(comment.description || '');
    }
  };

  const handleSaveComment = async () => {
    if (!editingCommentId || !editedCommentText.trim()) return;

    try {
      const commentToUpdate = comments.find(c => c.id === editingCommentId);
      if (!commentToUpdate) return;

      const updatedComment: CommentDTO = {
        ...commentToUpdate,
        description: editedCommentText
      };

      const response = await CommentsService.putApiCommentsById({
        id: editingCommentId,
        requestBody: updatedComment
      });

      // Update the comment in the list
      setComments(comments.map(c => c.id === editingCommentId ? response : c));

      // Reset editing state
      setEditingCommentId(null);
      setEditedCommentText('');

      enqueueSnackbar('Komentarz został zaktualizowany', { variant: 'success' });
    } catch (error) {
      console.error('Error updating comment:', error);
      enqueueSnackbar('Nie udało się zaktualizować komentarza', { variant: 'error' });
    }
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditedCommentText('');
  };

  const handleCommentChange = (value: string, mentionedIds: string[]) => {
    setNewComment(value);
    setMentionedUserIds(mentionedIds);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0 || !leadId) return;

    const file = event.target.files[0];
    await handleUploadFile(file);
  };

  const handleUploadFile = async (file: File) => {
    if (!leadId) return;

    try {
      await uploadFile({ 
        formData: { file: file },
        relatedObjectId: leadId.toString(),
        relatedObjectType: 'LEAD'
      });
      refetchFiles();
      
      // Refresh storage usage in sidebar
      queryClient.invalidateQueries({ queryKey: ['MinIoFileStorageServiceGetApiFilesStorageInfo'] });

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  // Drag and drop handlers
  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      // Only handle the first file for simplicity
      handleUploadFile(e.dataTransfer.files[0]);
    }
  };

  const handleDownloadFile = async (filename: string, originalFilename?: string) => {
    await downloadFile(filename, originalFilename);
  };

  const handleDeleteFile = async (file: FileMetadataDTO) => {
    deleteFile(file);
    refetchFiles();
  };

  // Show loading if data is being fetched OR if the loaded data doesn't match current leadId
  // This prevents showing stale data from previous contact while new data is loading
  if (loading || (lead && lead.id !== leadId)) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!lead) {
    return null;
  }

  return (
    <Dialog   sx={{ '& .MuiDialog-paper': { margin: 0 } }}
              open={open} onClose={onClose} maxWidth="md" fullWidth>
      {/* Acquisition Type Menu */}
      <Menu
        anchorEl={acquisitionTypeMenuAnchor}
        open={Boolean(acquisitionTypeMenuAnchor)}
        onClose={() => setAcquisitionTypeMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {isLoadingAcquisitionTypes ? (
          <MenuItem disabled>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            Ładowanie...
          </MenuItem>
        ) : (
          <>
            <MenuItem 
              onClick={() => handleAcquisitionTypeChange(null as any)}
              sx={{
                backgroundColor: !lead?.acquisitionType ? 'rgba(0, 0, 0, 0.08)' : 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Typography>Brak</Typography>
              {!lead?.acquisitionType && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
            </MenuItem>
            {acquisitionTypes?.map((type) => {
              const isCurrentType = lead?.acquisitionType?.id === type.id;
              return (
                <MenuItem 
                  key={type.id} 
                  onClick={() => handleAcquisitionTypeChange(type)}
                  sx={{
                    backgroundColor: isCurrentType ? 'rgba(0, 0, 0, 0.08)' : 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Typography>{type.name}</Typography>
                  {isCurrentType && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
                </MenuItem>
              );
            })}
          </>
        )}
      </Menu>

      {/* Offer Menu */}
      <Menu
        anchorEl={offerMenuAnchor}
        open={Boolean(offerMenuAnchor)}
        onClose={() => setOfferMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {isLoadingOffers ? (
          <MenuItem disabled>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            Ładowanie...
          </MenuItem>
        ) : (
          <>
            <MenuItem 
              onClick={() => handleOfferChange(null)}
              sx={{
                backgroundColor: !lead?.offer ? 'rgba(0, 0, 0, 0.08)' : 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Typography>Brak</Typography>
              {!lead?.offer && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
            </MenuItem>
            {offers?.map((offer) => {
              const isCurrentOffer = lead?.offer === offer.id;
              return (
                <MenuItem 
                  key={offer.id} 
                  onClick={() => handleOfferChange(offer.id || null)}
                  sx={{
                    backgroundColor: isCurrentOffer ? 'rgba(0, 0, 0, 0.08)' : 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Typography>{offer.name}</Typography>
                  {isCurrentOffer && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
                </MenuItem>
              );
            })}
          </>
        )}
      </Menu>

      <DialogTitle sx={{ py: 1, fontSize: '0.9rem', fontWeight: 600 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="inherit">
            Kontakt ID {lead.prefixId}-{lead.id}
          </Typography>
          <Box>
            <IconButton onClick={onClose} size="small" aria-label="close">
              <CancelIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent 
        ref={dialogContentRef}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        sx={{
          position: 'relative',
          py: 1,
          px: 2,
          overflowY: 'visible',
          ...(dragActive && {
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              border: '2px dashed #1976d2',
              borderRadius: '4px',
              zIndex: 9999,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pointerEvents: 'none',
            }
          })
        }}
      >
        {dragActive && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10000,
              backgroundColor: 'white',
              padding: 2,
              borderRadius: 1,
              boxShadow: 3,
              textAlign: 'center',
              pointerEvents: 'none',
            }}
          >
            <AttachFileIcon fontSize="large" color="primary" />
            <Typography variant="h6" color="primary">
              Upuść plik, aby dodać załącznik
            </Typography>
          </Box>
        )}
          <>

            <Box sx={{ width: '100%', minHeight: '450px' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={tabValue} 
                  onChange={handleTabChange} 
                  aria-label="lead details tabs"
                  sx={{ minHeight: '24px' }}
                >
                  <Tab
                      icon={<AssignmentIcon sx={{ fontSize: '0.85rem' }} />}
                      label="Szczegóły"
                      {...a11yProps(0)}
                      sx={{
                        minHeight: '36px',
                        py: 0.5,
                        fontSize: '0.8rem',
                        '& .MuiTab-iconWrapper': {
                          marginRight: '4px',
                          marginBottom: 0
                        },
                        flexDirection: 'row'
                      }}
                  />
                  <Tab
                      icon={<CommentIcon sx={{ fontSize: '0.85rem' }} />}
                      label="Komentarze"
                      {...a11yProps(1)}
                      sx={{
                        minHeight: '36px',
                        py: 0.5,
                        fontSize: '0.8rem',
                        '& .MuiTab-iconWrapper': {
                          marginRight: '4px',
                          marginBottom: 0
                        },
                        flexDirection: 'row'
                      }}
                  />
                  <Tab
                      icon={<AttachFileIcon sx={{ fontSize: '0.85rem' }} />}
                      label="Załączniki"
                      {...a11yProps(2)}
                      sx={{
                        minHeight: '36px',
                        py: 0.5,
                        fontSize: '0.8rem',
                        '& .MuiTab-iconWrapper': {
                          marginRight: '4px',
                          marginBottom: 0
                        },
                        flexDirection: 'row'
                      }}
                  />
                  <Tab
                      icon={<TaskIcon sx={{ fontSize: '0.85rem' }} />}
                      label="Zadania"
                      {...a11yProps(3)}
                      sx={{
                        minHeight: '36px',
                        py: 0.5,
                        fontSize: '0.8rem',
                        '& .MuiTab-iconWrapper': {
                          marginRight: '4px',
                          marginBottom: 0
                        },
                        flexDirection: 'row'
                      }}
                  />
                </Tabs>
              </Box>

              <TabPanel value={tabValue} index={0}>
                <Stack spacing={1.5} sx={{ p: 1.5 }}>
                  {/* Sekcja: Dane firmy */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <BusinessIcon fontSize="small" sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="subtitle2" fontSize="0.85rem" fontWeight={600}>
                      Dane firmy
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 2 }}>
                      {nameEditMode ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TextField
                            fullWidth
                            label="Nazwa firmy"
                            value={editedName}
                            onChange={(e) => setEditedName(e.target.value)}
                            variant="outlined"
                            size="small"
                            autoFocus
                            sx={{ mr: 1, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                          />
                          <IconButton onClick={handleSaveName} color="primary" size="small">
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton onClick={handleCancelNameEdit} color="default" size="small">
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <TextField
                          label="Nazwa firmy"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={lead.name || 'Brak'}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <EditIcon fontSize="small" sx={{ opacity: 0.5, width: '0.8em', height: '0.8em' }} />
                            )
                          }}
                          onClick={handleNameClick}
                          sx={{ 
                            cursor: 'pointer',
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#3f51b5',
                              }
                            },
                            '& .MuiInputBase-input': {
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      {nipEditMode ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TextField
                            fullWidth
                            label="NIP"
                            value={editedNip}
                            onChange={(e) => setEditedNip(e.target.value)}
                            variant="outlined"
                            size="small"
                            autoFocus
                            sx={{ mr: 1, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                          />
                          <IconButton onClick={handleSaveNip} color="primary" size="small">
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton onClick={handleCancelNipEdit} color="default" size="small">
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <TextField
                          label="NIP"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={lead.nip || 'Brak'}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <EditIcon fontSize="small" sx={{ opacity: 0.5, width: '0.8em', height: '0.8em' }} />
                            )
                          }}
                          onClick={handleNipClick}
                          sx={{ 
                            cursor: 'pointer',
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#3f51b5',
                              }
                            },
                            '& .MuiInputBase-input': {
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Sekcja: Dane osobowe */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PersonIcon fontSize="small" sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="subtitle2" fontSize="0.85rem" fontWeight={600}>
                      Dane osobowe
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      {firstNameEditMode ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TextField
                            fullWidth
                            label="Imię"
                            value={editedFirstName}
                            onChange={(e) => setEditedFirstName(e.target.value)}
                            variant="outlined"
                            size="small"
                            autoFocus
                            sx={{ mr: 1, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                          />
                          <IconButton onClick={handleSaveFirstName} color="primary" size="small">
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton onClick={handleCancelFirstNameEdit} color="default" size="small">
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <TextField
                          label="Imię"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={lead.firstName || 'Brak'}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <EditIcon fontSize="small" sx={{ opacity: 0.5, width: '0.8em', height: '0.8em' }} />
                            )
                          }}
                          onClick={handleFirstNameClick}
                          sx={{ 
                            cursor: 'pointer',
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#3f51b5',
                              }
                            },
                            '& .MuiInputBase-input': {
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      {lastNameEditMode ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TextField
                            fullWidth
                            label="Nazwisko"
                            value={editedLastName}
                            onChange={(e) => setEditedLastName(e.target.value)}
                            variant="outlined"
                            size="small"
                            autoFocus
                            sx={{ mr: 1, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                          />
                          <IconButton onClick={handleSaveLastName} color="primary" size="small">
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton onClick={handleCancelLastNameEdit} color="default" size="small">
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <TextField
                          label="Nazwisko"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={lead.lastName || 'Brak'}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <EditIcon fontSize="small" sx={{ opacity: 0.5, width: '0.8em', height: '0.8em' }} />
                            )
                          }}
                          onClick={handleLastNameClick}
                          sx={{ 
                            cursor: 'pointer',
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#3f51b5',
                              }
                            },
                            '& .MuiInputBase-input': {
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Email + Telefon + Alternatywny telefon */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 2 }}>
                      {emailEditMode ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TextField
                            fullWidth
                            label="Email"
                            value={editedEmail}
                            onChange={(e) => setEditedEmail(e.target.value)}
                            variant="outlined"
                            size="small"
                            autoFocus
                            sx={{ mr: 1, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                          />
                          <IconButton onClick={handleSaveEmail} color="primary" size="small">
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton onClick={handleCancelEmailEdit} color="default" size="small">
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <TextField
                          label="Email"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={lead.email || 'Brak'}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <EditIcon fontSize="small" sx={{ opacity: 0.5, width: '0.8em', height: '0.8em' }} />
                            )
                          }}
                          onClick={handleEmailClick}
                          sx={{ 
                            cursor: 'pointer',
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#3f51b5',
                              }
                            },
                            '& .MuiInputBase-input': {
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      {phoneEditMode ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TextField
                            fullWidth
                            label="Telefon"
                            value={editedPhone}
                            onChange={(e) => setEditedPhone(e.target.value)}
                            variant="outlined"
                            size="small"
                            autoFocus
                            sx={{ mr: 1, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                          />
                          <IconButton onClick={handleSavePhone} color="primary" size="small">
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton onClick={handleCancelPhoneEdit} color="default" size="small">
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <TextField
                          label="Telefon"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={lead.phone || 'Brak'}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <EditIcon fontSize="small" sx={{ opacity: 0.5, width: '0.8em', height: '0.8em' }} />
                            )
                          }}
                          onClick={handlePhoneClick}
                          sx={{ 
                            cursor: 'pointer',
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#3f51b5',
                              }
                            },
                            '& .MuiInputBase-input': {
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      {alternatePhoneEditMode ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TextField
                            fullWidth
                            label="Alternatywny telefon"
                            value={editedAlternatePhone}
                            onChange={(e) => setEditedAlternatePhone(e.target.value)}
                            variant="outlined"
                            size="small"
                            autoFocus
                            sx={{ mr: 1, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                          />
                          <IconButton onClick={handleSaveAlternatePhone} color="primary" size="small">
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton onClick={handleCancelAlternatePhoneEdit} color="default" size="small">
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <TextField
                          label="Alternatywny telefon"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={lead.alternatePhone || 'Brak'}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <EditIcon fontSize="small" sx={{ opacity: 0.5, width: '0.8em', height: '0.8em' }} />
                            )
                          }}
                          onClick={handleAlternatePhoneClick}
                          sx={{ 
                            cursor: 'pointer',
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#3f51b5',
                              }
                            },
                            '& .MuiInputBase-input': {
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Sekcja: Adres */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <HomeIcon fontSize="small" sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="subtitle2" fontSize="0.85rem" fontWeight={600}>
                      Adres
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      {cityEditMode ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TextField
                            fullWidth
                            label="Miasto"
                            value={editedCity}
                            onChange={(e) => setEditedCity(e.target.value)}
                            variant="outlined"
                            size="small"
                            autoFocus
                            sx={{ mr: 1, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                          />
                          <IconButton onClick={handleSaveCity} color="primary" size="small">
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton onClick={handleCancelCityEdit} color="default" size="small">
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <TextField
                          label="Miasto"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={lead.city || 'Brak'}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <EditIcon fontSize="small" sx={{ opacity: 0.5, width: '0.8em', height: '0.8em' }} />
                            )
                          }}
                          onClick={handleCityClick}
                          sx={{ 
                            cursor: 'pointer',
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#3f51b5',
                              }
                            },
                            '& .MuiInputBase-input': {
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      {postalCodeEditMode ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TextField
                            fullWidth
                            label="Kod pocztowy"
                            value={editedPostalCode}
                            onChange={(e) => setEditedPostalCode(e.target.value)}
                            variant="outlined"
                            size="small"
                            autoFocus
                            sx={{ mr: 1, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                          />
                          <IconButton onClick={handleSavePostalCode} color="primary" size="small">
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton onClick={handleCancelPostalCodeEdit} color="default" size="small">
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <TextField
                          label="Kod pocztowy"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={lead.postalCode || 'Brak'}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <EditIcon fontSize="small" sx={{ opacity: 0.5, width: '0.8em', height: '0.8em' }} />
                            )
                          }}
                          onClick={handlePostalCodeClick}
                          sx={{ 
                            cursor: 'pointer',
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#3f51b5',
                              }
                            },
                            '& .MuiInputBase-input': {
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Ulica + Numer domu + Numer mieszkania */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 2 }}>
                      {streetEditMode ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TextField
                            fullWidth
                            label="Ulica"
                            value={editedStreet}
                            onChange={(e) => setEditedStreet(e.target.value)}
                            variant="outlined"
                            size="small"
                            autoFocus
                            sx={{ mr: 1, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                          />
                          <IconButton onClick={handleSaveStreet} color="primary" size="small">
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton onClick={handleCancelStreetEdit} color="default" size="small">
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <TextField
                          label="Ulica"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={lead.street || 'Brak'}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <EditIcon fontSize="small" sx={{ opacity: 0.5, width: '0.8em', height: '0.8em' }} />
                            )
                          }}
                          onClick={handleStreetClick}
                          sx={{ 
                            cursor: 'pointer',
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#3f51b5',
                              }
                            },
                            '& .MuiInputBase-input': {
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      {houseNumberEditMode ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TextField
                            fullWidth
                            label="Numer domu"
                            value={editedHouseNumber}
                            onChange={(e) => setEditedHouseNumber(e.target.value)}
                            variant="outlined"
                            size="small"
                            autoFocus
                            sx={{ mr: 1, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                          />
                          <IconButton onClick={handleSaveHouseNumber} color="primary" size="small">
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton onClick={handleCancelHouseNumberEdit} color="default" size="small">
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <TextField
                          label="Numer domu"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={lead.houseNumber || 'Brak'}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <EditIcon fontSize="small" sx={{ opacity: 0.5, width: '0.8em', height: '0.8em' }} />
                            )
                          }}
                          onClick={handleHouseNumberClick}
                          sx={{ 
                            cursor: 'pointer',
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#3f51b5',
                              }
                            },
                            '& .MuiInputBase-input': {
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      {apartmentNumberEditMode ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TextField
                            fullWidth
                            label="Numer mieszkania"
                            value={editedApartmentNumber}
                            onChange={(e) => setEditedApartmentNumber(e.target.value)}
                            variant="outlined"
                            size="small"
                            autoFocus
                            sx={{ mr: 1, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                          />
                          <IconButton onClick={handleSaveApartmentNumber} color="primary" size="small">
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton onClick={handleCancelApartmentNumberEdit} color="default" size="small">
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <TextField
                          label="Numer mieszkania"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={lead.apartmentNumber || 'Brak'}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <EditIcon fontSize="small" sx={{ opacity: 0.5, width: '0.8em', height: '0.8em' }} />
                            )
                          }}
                          onClick={handleApartmentNumberClick}
                          sx={{ 
                            cursor: 'pointer',
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#3f51b5',
                              }
                            },
                            '& .MuiInputBase-input': {
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Sekcja: Dodatkowe informacje */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LabelIcon fontSize="small" sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="subtitle2" fontSize="0.85rem" fontWeight={600}>
                      Dodatkowe informacje
                    </Typography>
                  </Box>
                  
                  {/* Oferta i Źródło kontaktu */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <TextField
                        label="Źródło kontaktu"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={lead.acquisitionType?.name || 'Brak'}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <EditIcon fontSize="small" sx={{ opacity: 0.5, width: '0.8em', height: '0.8em' }} />
                          )
                        }}
                        onClick={(e) => setAcquisitionTypeMenuAnchor(e.currentTarget)}
                        sx={{ 
                          cursor: 'pointer',
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: '#3f51b5',
                            }
                          },
                          '& .MuiInputBase-input': {
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <TextField
                        label="Oferta"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={offers?.find(o => o.id === lead.offer)?.name || 'Brak'}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <EditIcon fontSize="small" sx={{ opacity: 0.5, width: '0.8em', height: '0.8em' }} />
                          )
                        }}
                        onClick={(e) => setOfferMenuAnchor(e.currentTarget)}
                        sx={{ 
                          cursor: 'pointer',
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: '#3f51b5',
                            }
                          },
                          '& .MuiInputBase-input': {
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Atrybuty - nowa implementacja z przyciskiem + */}
                  <Box>
                    {/* Wyświetl tylko zapisane atrybuty */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {Array.from(selectedAttributeKeys).map((attributeKey) => {
                        const predefinedAttr = predefinedAttributes?.find(attr => attr.attributeKey === attributeKey);
                        const attributeType = predefinedAttr?.attributeType;
                        const currentValue = attributes[attributeKey] || '';
                        const isInEditMode = attributeEditModes[attributeKey] || false;
                        const editedValue = editedAttributes[attributeKey] || '';

                        const renderAttributeInput = () => {
                          if (isInEditMode) {
                            // Edit mode - show editable input with save/cancel buttons
                            const commonEditSx = {
                              '& .MuiInputBase-input': { fontSize: '0.8rem' },
                              mr: 1,
                              flex: 1
                            };

                            let editInput;
                            switch (attributeType) {
                              case 'BOOLEAN':
                                editInput = (
                                  <FormControl variant="outlined" size="small" sx={{ mr: 1, minWidth: 120, flex: 1 }}>
                                    <InputLabel id={`${attributeKey}-edit-label`}>{attributeKey}</InputLabel>
                                    <Select
                                      labelId={`${attributeKey}-edit-label`}
                                      value={editedValue || ''}
                                      onChange={(e) => {
                                        setEditedAttributes(prev => ({
                                          ...prev,
                                          [attributeKey]: e.target.value
                                        }));
                                      }}
                                      label={attributeKey}
                                      autoFocus
                                      sx={{
                                        '& .MuiSelect-select': { fontSize: '0.8rem' },
                                        '& .MuiInputLabel-root': { fontSize: '0.8rem' }
                                      }}
                                    >
                                      <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Brak</MenuItem>
                                      <MenuItem value="true" sx={{ fontSize: '0.8rem' }}>Tak</MenuItem>
                                      <MenuItem value="false" sx={{ fontSize: '0.8rem' }}>Nie</MenuItem>
                                    </Select>
                                  </FormControl>
                                );
                                break;
                              case 'DATE':
                                editInput = (
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label={attributeKey}
                                    value={editedValue}
                                    onChange={(e) => {
                                      setEditedAttributes(prev => ({
                                        ...prev,
                                        [attributeKey]: e.target.value
                                      }));
                                    }}
                                    autoFocus
                                    InputLabelProps={{ shrink: true }}
                                    sx={commonEditSx}
                                  />
                                );
                                break;
                              case 'NUMBER':
                                editInput = (
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label={attributeKey}
                                    value={editedValue}
                                    onChange={(e) => {
                                      setEditedAttributes(prev => ({
                                        ...prev,
                                        [attributeKey]: e.target.value
                                      }));
                                    }}
                                    autoFocus
                                    sx={commonEditSx}
                                  />
                                );
                                break;
                              case 'TEXT':
                              default:
                                editInput = (
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="text"
                                    label={attributeKey}
                                    value={editedValue}
                                    onChange={(e) => {
                                      setEditedAttributes(prev => ({
                                        ...prev,
                                        [attributeKey]: e.target.value
                                      }));
                                    }}
                                    autoFocus
                                    sx={commonEditSx}
                                  />
                                );
                                break;
                            }

                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {editInput}
                                <IconButton onClick={() => handleSaveAttribute(attributeKey)} color="primary" size="small">
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                                <IconButton onClick={() => handleCancelAttributeEdit(attributeKey)} color="default" size="small">
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            );
                          } else {
                            // Display mode - show read-only field with edit icon
                            const displayValue = currentValue || 'Brak';
                            const commonDisplaySx = {
                              cursor: 'pointer',
                              '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                  borderColor: '#3f51b5',
                                }
                              },
                              '& .MuiInputBase-input': {
                                cursor: 'pointer',
                                fontSize: '0.8rem'
                              },
                              flex: 1
                            };

                            if (attributeType === 'BOOLEAN') {
                              return (
                                <TextField
                                  label={attributeKey}
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  value={currentValue === 'true' ? 'Tak' : currentValue === 'false' ? 'Nie' : 'Brak'}
                                  InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                      <EditIcon fontSize="small" sx={{ opacity: 0.5, width: '0.8em', height: '0.8em' }} />
                                    )
                                  }}
                                  onClick={() => handleAttributeClick(attributeKey)}
                                  sx={commonDisplaySx}
                                />
                              );
                            } else {
                              return (
                                <TextField
                                  label={attributeKey}
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  value={displayValue}
                                  InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                      <EditIcon fontSize="small" sx={{ opacity: 0.5, width: '0.8em', height: '0.8em' }} />
                                    )
                                  }}
                                  onClick={() => handleAttributeClick(attributeKey)}
                                  sx={commonDisplaySx}
                                />
                              );
                            }
                          }
                        };

                        return (
                          <Box key={attributeKey} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, width: 'calc(50% - 8px)' }}>
                            {renderAttributeInput()}
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveAttribute(attributeKey)}
                              sx={{ color: 'error.main' }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        );
                      })}
                    </Box>

                    {/* Przycisk dodawania atrybutu */}
                    {getAvailableAttributes().length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => setAttributeMenuAnchor(e.currentTarget)}
                          sx={{ 
                            color: 'primary.main',
                            border: '1px dashed',
                            borderColor: 'primary.main',
                            borderRadius: 1
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" sx={{ ml: 1, fontSize: '0.8rem', color: 'text.secondary' }}>
                          Dodaj atrybut
                        </Typography>
                      </Box>
                    )}

                    {/* Menu wyboru atrybutu */}
                    <Menu
                      anchorEl={attributeMenuAnchor}
                      open={Boolean(attributeMenuAnchor)}
                      onClose={() => setAttributeMenuAnchor(null)}
                    >
                      {getAvailableAttributes().map((attr) => (
                        <MenuItem
                          key={attr.attributeKey}
                          onClick={() => handleAddAttribute(attr.attributeKey || '')}
                          sx={{ fontSize: '0.8rem' }}
                        >
                          {attr.attributeKey}
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>


                </Stack>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <Box sx={{ mb: 2, p: 2 }}>
                  <MentionInput
                    value={newComment}
                    onChange={handleCommentChange}
                    placeholder="Dodaj komentarz... (użyj @ aby wspomnieć użytkownika)"
                    rows={3}
                    variant="outlined"
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.8rem' } }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.25 }}>
                    <Button 
                      variant="contained" 
                      color="primary"
                      size="small"
                      onClick={handleAddComment}
                      disabled={!newComment.trim()}
                      sx={{ py: 0.25, px: 1, fontSize: '0.7rem' }}
                    >
                      Dodaj komentarz
                    </Button>
                  </Box>
                </Box>

                <Divider sx={{ my: 0.5 }} />

                {loadingComments ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={16} />
                  </Box>
                ) : comments.length === 0 ? (
                  <Typography align="center" color="text.secondary" variant="caption" sx={{ py: 0.5, display: 'block' }}>
                    Brak komentarzy
                  </Typography>
                ) : (
                  <>
                    <List dense sx={{ py: 0 }}>
                      {comments
                        .slice(0, showAllComments ? comments.length : 4)
                        .map((comment, index) => (
                          <React.Fragment key={comment.id}>
                            <Box 
                              data-comment-id={comment.id}
                              sx={{ 
                                mb: 0.5, 
                                py: 0.5, 
                                backgroundColor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'rgba(0, 0, 0, 0.08)',
                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                overflow: 'hidden'
                              }}
                            >
                              <Box 
                                sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  mb: 0.25,
                                  cursor: 'pointer',
                                  backgroundColor: 'rgba(63, 81, 181, 0.08)',
                                  px: 1,
                                  py: 0.5
                                }}
                                onClick={() => comment.id && toggleCommentExpand(comment.id)}
                              >
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    fontSize: '0.75rem', 
                                    fontStyle: 'italic',
                                    color: 'rgba(0, 0, 0, 0.7)'
                                  }}
                                >
                                  Użytkownik <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : ''}</Box> dodał komentarz - {format(new Date(comment.createdAt || ''), 'dd MMM yyyy HH:mm', { locale: pl })}
                                </Typography>
                                <IconButton 
                                  size="small" 
                                  title={expandedComments[comment.id || 0] ? "Zwiń" : "Rozwiń"}
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent triggering the parent onClick
                                    comment.id && toggleCommentExpand(comment.id);
                                  }}
                                  sx={{ p: 0.25, ml: 0.5 }}
                                >
                                  <KeyboardArrowDownIcon 
                                    sx={{ 
                                      fontSize: '0.75rem',
                                      transform: expandedComments[comment.id || 0] ? 'rotate(180deg)' : 'rotate(0deg)',
                                      transition: 'transform 0.3s'
                                    }} 
                                  />
                                </IconButton>
                              </Box>
                              {expandedComments[comment.id || 0] && (
                                <Box sx={{ px: 1, py: 0.5 }}>
                                  {editingCommentId === comment.id ? (
                                    <>
                                      <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        value={editedCommentText}
                                        onChange={(e) => setEditedCommentText(e.target.value)}
                                        variant="outlined"
                                        size="small"
                                        sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: '0.8rem' } }}
                                      />
                                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                        <Button
                                          size="small"
                                          variant="contained"
                                          color="primary"
                                          onClick={handleSaveComment}
                                          disabled={!editedCommentText.trim()}
                                          startIcon={<SaveIcon fontSize="small" />}
                                          sx={{ py: 0.25, px: 1, fontSize: '0.7rem' }}
                                        >
                                          Zapisz
                                        </Button>
                                        <Button
                                          size="small"
                                          onClick={handleCancelEditComment}
                                          startIcon={<CancelIcon fontSize="small" />}
                                          sx={{ py: 0.25, px: 1, fontSize: '0.7rem' }}
                                        >
                                          Anuluj
                                        </Button>
                                      </Box>
                                    </>
                                  ) : (
                                    <>
                                      <MentionText 
                                        text={comment.description || ''}
                                        variant="caption"
                                        sx={{ fontSize: '0.8rem' }}
                                      />
                                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                        <IconButton 
                                          size="small" 
                                          color="primary"
                                          onClick={() => handleEditComment(comment)}
                                          sx={{ p: 0.25 }}
                                        >
                                          <CreateIcon sx={{ fontSize: '0.9rem' }} />
                                        </IconButton>
                                        <IconButton 
                                          size="small" 
                                          color="error"
                                          onClick={() => comment.id && handleDeleteComment(comment.id)}
                                          sx={{ p: 0.25 }}
                                        >
                                          <DeleteIcon sx={{ fontSize: '0.9rem' }} />
                                        </IconButton>
                                      </Box>
                                    </>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </React.Fragment>
                      ))}
                    </List>
                    {comments.length > 4 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                        <Button 
                          size="small" 
                          onClick={() => setShowAllComments(!showAllComments)}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          {showAllComments ? 'Pokaż mniej' : 'Zobacz więcej'}
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <Box sx={{ mb: 0.5, mt: 2, p: 2 }}>
                  <input
                    accept="*/*"
                    style={{ display: 'none' }}
                    id="raised-button-file"
                    type="file"
                    onChange={handleFileUpload}
                    ref={fileInputRef}
                  />
                  <label htmlFor="raised-button-file">
                    <Button
                      variant="contained"
                      component="span"
                      size="small"
                      startIcon={<AttachFileIcon sx={{ fontSize: '0.85rem' }} />}
                      disabled={isUploading}
                      sx={{ py: 0.5, px: 1, fontSize: '0.7rem' }}
                    >
                      {isUploading ? 'Dodawanie...' : 'Dodaj załącznik'}
                    </Button>
                  </label>
                </Box>

                <Divider sx={{ my: 0.5 }} />

                <FileList
                  files={files}
                  isLoading={loadingFiles}
                  onDownload={handleDownloadFile}
                  onDelete={handleDeleteFile}
                  onRestore={restoreFile}
                  compact={true}
                  enablePreview={true}
                />
              </TabPanel>

              <TabPanel value={tabValue} index={3}>
                <Box sx={{ p: 1.5 }}>
                  {/* Przycisk dodawania nowego zadania */}
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
                    <NewTaskButton 
                      refreshTasks={async () => {
                        await refetchTasks();
                      }}
                      preAssignedLeadId={leadId || undefined}
                    />
                  </Box>
                  
                  {loadingTasks ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : !leadTasks || (Array.isArray(leadTasks) && leadTasks.length === 0) ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                        Brak zadań przypisanych do tego leada
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>ID</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Tytuł</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Status</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Przypisane do użytkownika</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(Array.isArray(leadTasks) ? leadTasks : [leadTasks]).map((task) => (
                            <TableRow
                              key={task.id}
                              sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                              }}
                              onClick={() => {
                                if (task.id) {
                                  setSelectedTaskId(task.id);
                                  setOpenTaskDetailDialog(true);
                                }
                              }}
                            >
                              <TableCell sx={{ fontSize: '0.8rem' }}>
                                {task.prefixId} {task.id}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.8rem' }}>
                                {task.title}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={getStatusLabel(task.status)}
                                  sx={{
                                    backgroundColor: getStatusColor(task.status),
                                    color: 'white',
                                    fontSize: '0.75rem'
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Tooltip title={getUserFullName(task.assignedToUserId)}>
                                  <Avatar
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      bgcolor: getUserColor(task.assignedToUserId),
                                      fontSize: '0.75rem',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    {task.assignedToUserId ? getUserInitials(task.assignedToUserId) : <PersonIcon fontSize="small" />}
                                  </Avatar>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </TabPanel>
            </Box>
          </>

          {/* Informacje o utworzeniu/aktualizacji - przeniesione na dół dialogu */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #eee', fontSize: '0.75rem', px: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              <Box component="span" sx={{ fontWeight: 'bold', mr: 0.5 }}>Utworzono:</Box> 
              {lead.createdAt ? format(new Date(lead.createdAt), 'dd MMM yyyy HH:mm', { locale: pl }) : 'Brak daty'}
            </Typography>
            {lead.updatedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                <Box component="span" sx={{ fontWeight: 'bold', mr: 0.5 }}>Zaktualizowano:</Box> 
                {format(new Date(lead.updatedAt), 'dd MMM yyyy HH:mm', { locale: pl })}
              </Typography>
            )}
          </Box>
      </DialogContent>

      {/* Task Detail Dialog */}
      <TaskDetailDialog
        open={openTaskDetailDialog}
        taskId={selectedTaskId}
        onClose={() => {
          setOpenTaskDetailDialog(false);
          setSelectedTaskId(null);
        }}
        onTaskUpdated={() => {
          // Clear paginated task cache to ensure fresh data after task updates
          queryClient.invalidateQueries({ 
            queryKey: [useTaskRetrievalServiceGetApiLeadTasksLeadByLeadIdTasksKey]
          });
          
          // Also dispatch global event to refresh TasksPage if it's open
          window.dispatchEvent(new CustomEvent('refresh-tasks'));
        }}
      />

    </Dialog>
  );
};

export default LeadDetailDialog;
